import {
  arcDash,
  buildScoreBands,
  clampPercent,
  PROJECTED_BAND_OPACITY,
  tierColorOf,
  TRACK_COLOR,
} from "@/client/pages/coach/optimize/coach-score-visuals";

import { useAnimatedNumber } from "./use-animated-number";

const SIZE = 132;
const STROKE = 11;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * One donut showing where the résumé scores now and where the suggestions can take it.
 *
 * Two STACKED arcs on one uniform ring, both in the exact student-side tier colour: `0 → projected`
 * dulled in the projected tier's colour, with `0 → current` at full strength in the current tier's
 * colour drawn over the top of it. The centre reads the current score, so accepting a suggestion grows
 * the opaque arc over the dulled one — the movement and the number are the same gesture.
 *
 * ⚠️ STACKED, NOT END TO END, AND NEITHER ARC IS EVER OMITTED. Both circles stay mounted at every
 * score, including when the two are the same length. That is what makes the final accept animate: an
 * omitted band unmounts its `<circle>`, so the reach used to disappear in one frame while the earned
 * arc was still growing into it. See buildScoreBands for the full account.
 *
 * ⚠️ ONE STROKE WIDTH AND NO GAP. The reachable arc is marked as not-yet-yours by stroke opacity
 * alone (PROJECTED_BAND_OPACITY), and the earned arc is fully opaque, which is what lets it cover
 * rather than blend with the arc beneath. A blank gap between the arcs and a thinner reachable arc
 * were both tried and both made the ring look defective rather than segmented.
 *
 * Arc layout and colour live in coach-score-visuals.ts; this file is geometry and markup only.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const CoachScoreDonut = ({
  current,
  projected,
  label,
  showLabel = true,
}: {
  /** Score as it stands, including everything accepted so far. */
  current: number;
  /** Score if every remaining suggestion were accepted. */
  projected: number;
  /** Tier name for the current score — "Strong Match" / "Good Match" / "Stretch Role". */
  label: string;
  /**
   * Render the tier name under the percentage.
   *
   * False for a session with no LMS job: the tier is a verdict on a student against a job, and that
   * flow scores a résumé against a job description with no student profile in it. The label is still
   * used in the `aria-label` either way, because "48 percent" alone tells a screen-reader user less
   * than the sighted reader gets from the arc's colour.
   */
  showLabel?: boolean;
}) => {
  const currentPct = clampPercent(current);
  const projectedPct = Math.max(currentPct, clampPercent(projected));
  // Rendered in array order, which IS paint order: the reachable arc first, the earned arc over it.
  const bands = buildScoreBands({ current: currentPct, projected: projectedPct });
  // By key, not by index. The array is ordered for painting, and the centre figure takes the colour of
  // the score it is printing — reading `bands[0]` would take it from whichever arc happens to be
  // underneath.
  const currentColor =
    bands.find((band) => band.key === "current")?.color ?? tierColorOf(currentPct);

  // The printed number counts to its new value instead of swapping. The arcs are animated by CSS over
  // a similar period, so the ring and the figure move as one.
  //
  // ⚠️ ONLY THE DISPLAYED FIGURE IS ANIMATED. Band geometry and the aria-label below use `currentPct`,
  // the settled value — tweening the bands here would fight the CSS transition, and an aria-label that
  // changed sixty times a second would flood a screen reader with intermediate scores.
  const animatedPct = useAnimatedNumber(currentPct);

  return (
    <div className="relative mx-auto" style={{ width: SIZE, height: SIZE }}>
      <svg
        width={SIZE}
        height={SIZE}
        // -90deg puts 0% at twelve o'clock; arcs then run clockwise.
        className="-rotate-90"
        role="img"
        aria-label={
          projectedPct > currentPct
            ? `${Math.round(currentPct)} percent, ${label}. Up to ${Math.round(projectedPct)} percent available from the suggestions.`
            : `${Math.round(currentPct)} percent, ${label}`
        }
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={TRACK_COLOR}
          strokeWidth={STROKE}
        />

        {bands.map((band) => {
          const dash = arcDash({ from: band.from, to: band.to, circumference: CIRCUMFERENCE });

          return (
            <circle
              key={band.key}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={band.color}
              // The reachable arc is dulled; the earned one is full strength. Opacity is the only
              // difference between them besides hue — no gap, and the same stroke width, because a
              // thinner reachable arc notched the ring's outline and read as a rendering defect.
              strokeOpacity={band.key === "projected" ? PROJECTED_BAND_OPACITY : 1}
              strokeWidth={STROKE}
              // butt, not round: a round cap overhangs the arc end by half the stroke, which both
              // overstates the earned share and makes the two arcs overlap.
              strokeLinecap="butt"
              strokeDasharray={dash.strokeDasharray}
              strokeDashoffset={dash.strokeDashoffset}
              className="transition-[stroke-dasharray,stroke-dashoffset] duration-500 ease-out"
            />
          );
        })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-y-0.5">
        {/* `tabular-nums` so the digits keep a fixed width while counting — proportional figures make
            the number jitter horizontally as it climbs through different glyphs. */}
        <span
          className="text-2xl font-bold tabular-nums leading-none"
          style={{ color: currentColor }}
        >
          {Math.round(animatedPct)}%
        </span>
        {showLabel && (
          <span className="text-[10px] font-medium leading-none" style={{ color: currentColor }}>
            {label}
          </span>
        )}
      </div>
    </div>
  );
};
